import React, { useMemo } from "react";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import usePifReferenceData from "../../hooks/usePifReferenceData";
import usePifPositionRules from "../../hooks/usePifPositionRules";
import usePifSettings from "../../hooks/usePifSettings";
import WorkflowPifDetailsStage from "./WorkflowPifDetailsStage";
import { PifDetailsReference, PifOption } from "./pifDetailsModel";
import { PIF_DETAILS_FIELDS } from "./pifWorkflowKeys";
import { PostIfLookupItemDto } from "../../types/pifDto";
import { isCommunityDsp, isFullTimeAllowed } from "../../pages/supervision/PostInterviewForm/pifValidation";

type WorkflowPifDetailsLiveProps = Omit<React.ComponentProps<typeof WorkflowPifDetailsStage>, "reference">;

const toOptions = (items: PostIfLookupItemDto[]): PifOption[] =>
  items.map((item) => ({ id: String(item.id), text: item.text ?? "" }));

const toId = (value: string | undefined): number | null => {
  const id = Number(value);
  return value && Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * pif-3 on a real case: the same screen, fed by the PostIF reference routes — departments, the
 * department's positions (narrowed to the caller's Can Hire scope), reports-to, services and
 * clients, rate types, the position's rules (band, driving, Community DSP, full time) and the
 * "email required" setting. The answers already hold IDs, so the routes are keyed straight off them.
 *
 * A separate component so the reference hooks only ever mount once pif-3 is on screen.
 */
const WorkflowPifDetailsLive: React.FC<WorkflowPifDetailsLiveProps> = (props) => {
  const { values } = props;
  const departmentId = toId(values[PIF_DETAILS_FIELDS.department]);
  const positionId = departmentId ? toId(values[PIF_DETAILS_FIELDS.position]) : null;
  const serviceId = toId(values[PIF_DETAILS_FIELDS.service]);

  const { departments, positions, reportToUsers, services, clients, rateTypes, loading } = usePifReferenceData({
    departmentId,
    serviceId,
  });
  const { rules } = usePifPositionRules({ departmentId, positionId });
  const { emailRequired } = usePifSettings();

  const reference = useMemo<PifDetailsReference>(
    () => ({
      departments: toOptions(departments),
      positions: departmentId ? toOptions(positions) : [],
      reportsTo: toOptions(reportToUsers),
      rateTypes: toOptions(rateTypes),
      services: toOptions(services),
      clients: toOptions(clients),
      // A missing band is "unknown", never zero — or every rate would read as out of band.
      band:
        positionId &&
        rules &&
        rules.hiringMin !== null &&
        rules.hiringMin !== undefined &&
        rules.hiringMax !== null &&
        rules.hiringMax !== undefined
          ? { min: rules.hiringMin, max: rules.hiringMax }
          : null,
      driving: Boolean(positionId && rules?.mayOperateVehicle),
      communityDsp: Boolean(positionId) && isCommunityDsp(rules),
      fullTimeAllowed: Boolean(positionId) && isFullTimeAllowed(rules),
      emailRequired,
    }),
    [departments, departmentId, positions, positionId, rules, reportToUsers, rateTypes, services, clients, emailRequired],
  );

  // pif-3's display names are not written here: the server derives them from these IDs once pif-3 is
  // submitted (useWorkflowPifDisplayNames), so approvers never sign off on text this screen supplied.
  // The dropdowns are empty until their lists arrive (and a department change reloads three of
  // them), so the form says it is loading rather than looking like it has nothing to offer.
  return (
    <>
      <BackdropProgressBar loading={loading} />
      <WorkflowPifDetailsStage {...props} reference={reference} />
    </>
  );
};

export default WorkflowPifDetailsLive;
